import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { SaveOperationsProps, SaveOperationsReturn } from '../types';

export function useSaveOperations({
  searchItems,
  view,
  tab,
  source,
  isEvidence,
  totalResults,
  columnVisibilityModel,
  columnOrder,
  sourceWeights,
  getFilterValueRef,
  onSuccess,
  onError,
}: SaveOperationsProps): SaveOperationsReturn {
  const location = useLocation();

  // Search saving
  const [isSavingSearch, setIsSavingSearch] = useState(false);
  const [showSaveSearchModal, setShowSaveSearchModal] = useState(false);
  const [searchName, setSearchName] = useState('');
  const [searchDataToSave, setSearchDataToSave] = useState<any>(null);

  // Column saving
  const [isSavingColumns, setIsSavingColumns] = useState(false);
  const [showSaveColumnsModal, setShowSaveColumnsModal] = useState(false);
  const [columnSelectionName, setColumnSelectionName] = useState('');
  const [columnDataToSave, setColumnDataToSave] = useState<any>(null);

  // Score saving
  const [isSavingScore, setIsSavingScore] = useState(false);
  const [showSaveScoreModal, setShowSaveScoreModal] = useState(false);
  const [scoreName, setScoreName] = useState('');

  const titleGrid = isEvidence ? 'Evidence' : 'Summary';

  const prepareSearchSave = (saveType: 'filters' | 'complete' = 'filters') => {
    const allFilters = getFilterValueRef.current ? getFilterValueRef.current() : [];
    const contextParams = ['view', 'tab', 'source', 'idents'];
    const columnFilters = allFilters.filter(
      (f: any) =>
        !contextParams.includes(f.name) &&
        f.value !== undefined &&
        f.value !== null &&
        f.value !== '' &&
        !(Array.isArray(f.value) && f.value.length === 0),
    );

    const urlParams = new URLSearchParams(location.search);
    const searchQuery = `view=${view}&tab=${tab}&source=${source}&idents=${urlParams.get('idents') || 'ALL'}&isEvidence=${isEvidence}`;

    const searchItemsText =
      searchItems && searchItems.length > 0
        ? searchItems.map(item => item.name || item.id).join(', ')
        : 'Search';

    const defaultSearchName = `${searchItemsText} - ${titleGrid} (${new Date().toLocaleDateString()})`;

    const saveData = {
      columnFilters,
      searchQuery,
      isEvidence,
      searchItems: searchItems || [],
      view,
      tab,
      source,
      totalResults,
      saveType,
      ...(saveType === 'complete' && {
        columnVisibilityModel,
        columnOrder,
      }),
    };

    setSearchDataToSave(saveData);
    setSearchName(defaultSearchName);
    setShowSaveSearchModal(true);
  };

  const handleSaveSearch = () => prepareSearchSave('filters');
  const handleSaveComplete = () => prepareSearchSave('complete');

  const confirmSaveSearch = async () => {
    if (!searchDataToSave || !searchName.trim() || isSavingSearch) return;

    setIsSavingSearch(true);

    try {
      if (searchDataToSave.saveType === 'complete') {
        try {
          const { saveCompleteSearch } = await import('../../../../../../services/savedFiltersService');
          await saveCompleteSearch(
            searchName.trim(),
            searchDataToSave.columnFilters,
            searchDataToSave.searchQuery,
            searchDataToSave.columnVisibilityModel,
            searchDataToSave.columnOrder,
            {
              view: searchDataToSave.view,
              tab: searchDataToSave.tab,
              source: searchDataToSave.source,
              isEvidence: searchDataToSave.isEvidence,
              searchItems: searchDataToSave.searchItems,
            },
          );
        } catch (dbError) {
          console.warn('⚠️ DynamoDB save failed, falling back to localStorage:', dbError);
          const timestamp = new Date().getTime();
          const completeSearchKey = `completeSearch_${timestamp}`;
          const completeSearch = {
            name: searchName.trim(),
            columnFilters: searchDataToSave.columnFilters,
            searchQuery: searchDataToSave.searchQuery,
            columnVisibilityModel: searchDataToSave.columnVisibilityModel,
            columnOrder: searchDataToSave.columnOrder,
            savedAt: new Date().toISOString(),
            searchContext: {
              view: searchDataToSave.view,
              tab: searchDataToSave.tab,
              source: searchDataToSave.source,
              isEvidence: searchDataToSave.isEvidence,
              searchItems: searchDataToSave.searchItems,
            },
          };
          localStorage.setItem(completeSearchKey, JSON.stringify(completeSearch));
        }
      } else {
        const { saveFilter } = await import('../../../../../../services/savedFiltersService');
        await saveFilter(
          searchName.trim(),
          searchDataToSave.columnFilters,
          searchDataToSave.isEvidence,
          searchDataToSave.searchQuery,
        );
      }

      onSuccess(
        searchDataToSave.saveType === 'complete'
          ? 'Complete search saved successfully!'
          : 'Search saved successfully!',
      );
      setShowSaveSearchModal(false);
      setSearchDataToSave(null);
      setSearchName('');
    } catch (error) {
      console.error('Error saving search:', error);
      onError('Error saving search. Please try again.');
    } finally {
      setIsSavingSearch(false);
    }
  };

  const handleSaveColumnSelection = () => {
    if (!columnVisibilityModel) return;

    const defaultColumnName = `${view} - ${tab} ${isEvidence ? '(Evidence)' : '(Summary)'} - Columns (${new Date().toLocaleDateString()})`;
    const hasActiveSearch = searchItems && searchItems.length > 0 && searchItems[0]?.id !== 'ALL';

    setColumnDataToSave({
      columnVisibilityModel,
      columnOrder,
      view,
      tab,
      isEvidence,
      searchItems: hasActiveSearch ? searchItems?.map(item => ({ id: item.id, name: item.name })) : undefined,
    });
    setColumnSelectionName(defaultColumnName);
    setShowSaveColumnsModal(true);
  };

  const confirmSaveColumnSelection = async () => {
    if (!columnDataToSave || !columnSelectionName.trim() || isSavingColumns) return;

    setIsSavingColumns(true);

    try {
      try {
        const { saveColumnSelection } = await import('../../../../../../services/savedFiltersService');
        await saveColumnSelection(
          columnSelectionName.trim(),
          columnDataToSave.columnVisibilityModel,
          columnDataToSave.columnOrder,
          {
            view: columnDataToSave.view,
            tab: columnDataToSave.tab,
            isEvidence: columnDataToSave.isEvidence,
            searchItems: columnDataToSave.searchItems,
          },
        );
      } catch (dbError) {
        console.warn('⚠️ DynamoDB save failed, falling back to localStorage:', dbError);
        const timestamp = new Date().getTime();
        const columnSelectionKey = `columnSelection_${timestamp}`;
        const columnSelection = {
          name: columnSelectionName.trim(),
          columnVisibilityModel: columnDataToSave.columnVisibilityModel,
          columnOrder: columnDataToSave.columnOrder,
          savedAt: new Date().toISOString(),
          searchContext: {
            view: columnDataToSave.view,
            tab: columnDataToSave.tab,
            isEvidence: columnDataToSave.isEvidence,
            searchItems: columnDataToSave.searchItems,
          },
        };
        localStorage.setItem(columnSelectionKey, JSON.stringify(columnSelection));
      }

      onSuccess('Column selection saved successfully!');
      setShowSaveColumnsModal(false);
      setColumnDataToSave(null);
      setColumnSelectionName('');
    } catch (error) {
      console.error('Error saving column selection:', error);
      onError('Error saving column selection. Please try again.');
    } finally {
      setIsSavingColumns(false);
    }
  };

  const handleSaveScore = () => {
    if (!sourceWeights || sourceWeights.length === 0) {
      onError('No custom scores to save');
      return;
    }

    const searchItemsText =
      searchItems && searchItems.length > 0
        ? searchItems.map(item => item.name || item.id).join(', ')
        : 'Score';

    const defaultScoreName = `${searchItemsText} - Score (${new Date().toLocaleDateString()})`;
    setScoreName(defaultScoreName);
    setShowSaveScoreModal(true);
  };

  const confirmSaveScore = async () => {
    if (!scoreName.trim() || isSavingScore) return;

    setIsSavingScore(true);

    try {
      try {
        const { saveScore } = await import('../../../../../../services/savedFiltersService');
        await saveScore(scoreName.trim(), sourceWeights, {
          view,
          tab,
          source,
          searchItems: searchItems?.map(item => ({ id: item.id, name: item.name })),
        });
      } catch (dbError) {
        console.warn('⚠️ DynamoDB save failed, falling back to localStorage:', dbError);
        const timestamp = new Date().getTime();
        const scoreKey = `savedScore_${timestamp}`;
        const scoreData = {
          name: scoreName.trim(),
          sourceWeights,
          savedAt: new Date().toISOString(),
          searchContext: {
            view,
            tab,
            source,
            searchItems: searchItems?.map(item => ({ id: item.id, name: item.name })),
          },
        };
        localStorage.setItem(scoreKey, JSON.stringify(scoreData));
      }

      onSuccess('Score saved successfully!');
      setShowSaveScoreModal(false);
      setScoreName('');
    } catch (error) {
      console.error('Error saving score:', error);
      onError('Error saving score. Please try again.');
    } finally {
      setIsSavingScore(false);
    }
  };

  return {
    // Search saving
    isSavingSearch,
    showSaveSearchModal,
    setShowSaveSearchModal,
    searchName,
    setSearchName,
    searchDataToSave,
    handleSaveSearch,
    handleSaveComplete,
    confirmSaveSearch,

    // Column saving
    isSavingColumns,
    showSaveColumnsModal,
    setShowSaveColumnsModal,
    columnSelectionName,
    setColumnSelectionName,
    columnDataToSave,
    handleSaveColumnSelection,
    confirmSaveColumnSelection,

    // Score saving
    isSavingScore,
    showSaveScoreModal,
    setShowSaveScoreModal,
    scoreName,
    setScoreName,
    handleSaveScore,
    confirmSaveScore,
  };
}
